import { useState, useEffect } from 'react'
import { webauthnApi, type WebAuthnCredentialInfo } from '../api/webauthn'
import { getErrorMessage } from '@/shared/api/client'
import { toast } from '@/shared/stores/toastStore'

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export function WebAuthnSettings() {
  const [creds, setCreds] = useState<WebAuthnCredentialInfo[]>([])
  const [name, setName] = useState('')
  const [registering, setRegistering] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const supported = typeof window !== 'undefined' && !!window.PublicKeyCredential

  useEffect(() => {
    webauthnApi.listCredentials().then(setCreds).catch(() => {})
  }, [])

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    setLoading(true)
    try {
      const options = await webauthnApi.registerBegin()
      const publicKey: PublicKeyCredentialCreationOptions = {
        ...options,
        challenge: typeof options.challenge === 'string'
          ? base64urlToBuffer(options.challenge as unknown as string)
          : options.challenge,
        user: {
          ...options.user,
          id: typeof options.user.id === 'string'
            ? base64urlToBuffer(options.user.id as unknown as string)
            : options.user.id,
        },
        excludeCredentials: options.excludeCredentials?.map(c => ({
          ...c,
          id: typeof c.id === 'string' ? base64urlToBuffer(c.id as unknown as string) : c.id,
        })),
      }
      const credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential
      if (!credential) {
        setError('Registration cancelled')
        setLoading(false)
        return
      }
      await webauthnApi.registerFinish(name.trim(), credential)
      const updated = await webauthnApi.listCredentials()
      setCreds(updated)
      setName('')
      setRegistering(false)
      toast.info('Passkey registered')
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleRemove(credName: string) {
    try {
      await webauthnApi.removeCredential(credName)
      setCreds(prev => prev.filter(c => c.name !== credName))
      toast.info('Passkey removed')
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }

  if (!supported) return null

  return (
    <div data-testid="webauthn-settings">
      <h3>Passkeys</h3>
      {creds.length === 0 && !registering && (
        <p className="muted">No passkeys registered. Add one for faster login.</p>
      )}
      {creds.length > 0 && (
        <ul className="credential-list">
          {creds.map(c => (
            <li key={c.name} className="credential-item">
              <span>🔑 {c.name}</span>
              <span className="muted">{new Date(c.created_at).toLocaleDateString()}</span>
              <button className="btn-outline-danger btn-sm" onClick={() => handleRemove(c.name)} aria-label={`Remove ${c.name}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {registering ? (
        <form onSubmit={handleRegister}>
          <label htmlFor="passkey-name">Passkey name</label>
          <input
            id="passkey-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. MacBook Pro"
            required
            autoFocus
          />
          {error && <p className="text-danger">{error}</p>}
          <div className="button-row">
            <button type="button" className="btn-secondary" onClick={() => { setRegistering(false); setError('') }}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Registering...' : 'Register passkey'}
            </button>
          </div>
        </form>
      ) : (
        <button className="btn-primary" onClick={() => setRegistering(true)}>Add passkey</button>
      )}
    </div>
  )
}
